import React, { useState, ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogClose, DialogPortal } from './dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FullScreenVideoProps {
  src: string;
  className?: string;
  videoClassName?: string;
  modalClassName?: string;
  hideCloseButton?: boolean;
  children?: ReactNode;
}

// Custom DialogContent without the built-in close button
const CustomDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Overlay
      className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
CustomDialogContent.displayName = 'CustomDialogContent';

export function FullScreenVideo({
  src,
  className = '',
  videoClassName = '',
  modalClassName = '',
  hideCloseButton = false,
  children,
}: FullScreenVideoProps) {
  const [isOpen, setIsOpen] = useState(false);

  const openFullScreen = () => setIsOpen(true);
  const closeFullScreen = () => setIsOpen(false);

  return (
    <>
      {children ? (
        <div className={className} onClick={openFullScreen}>
          {children}
        </div>
      ) : (
        <div className={className} onClick={openFullScreen}>
          <video
            className={`cursor-pointer ${videoClassName}`}
            loop
            autoPlay
            playsInline
            muted
          >
            <source src={src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={closeFullScreen}>
        <CustomDialogContent 
          className={`p-0 max-w-screen-lg w-full flex items-center justify-center bg-transparent border-none ${modalClassName}`}
        >
          {!hideCloseButton && (
            <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-50">
              <X className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </DialogClose>
          )}
          <div className="overflow-auto max-h-[90vh] max-w-full p-2">
            <video
              className="max-h-[85vh] max-w-full object-contain"
              loop
              autoPlay
              playsInline
              controls
            >
              <source src={src} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </CustomDialogContent>
      </Dialog>
    </>
  );
} 